/* @jsx h */
import { defineComponent, h } from "vue";

export type ResponsiveTableColumn = {
  key: string;
  label: string;
  icon?: string;
  headerClass?: string;
  cellClass?: string;
  render: (row: any) => any;
  mobilePriority?: "primary" | "secondary" | "actions" | "hidden";
  /** When true, column is skipped in the desktop table but still used for mobile cards. */
  desktopHide?: boolean;
  /** Overrides the flex wrapper class in the primary zone (default: "flex-1 min-w-0"). */
  mobilePrimaryClass?: string;
};

export type ResponsiveTableProps = {
  columns: ResponsiveTableColumn[];
  rows: any[];
  rowKey: (row: any) => string | number;
  onRowClick?: (row: any) => void;
  /** Renders a row across the full table width. Return null for regular data rows. */
  renderFullWidthRow?: (row: any) => any | null;
  /** Separate click handler for mobile cards. Falls back to onRowClick if not provided. */
  onMobileCardClick?: (row: any) => void;
  loading?: boolean;
  loadingMore?: boolean;
  emptyMessage?: string;
  /** Set false when the table is already inside a scrolling page shell. */
  stickyHeader?: boolean;
};

export const ResponsiveTable = defineComponent<ResponsiveTableProps>({
  name: "ResponsiveTable",
  props: [
    "columns",
    "rows",
    "rowKey",
    "onRowClick",
    "renderFullWidthRow",
    "onMobileCardClick",
    "loading",
    "loadingMore",
    "emptyMessage",
    "stickyHeader",
  ],
  setup(props) {
    // ─── Desktop table ───────────────────────────────────────────────────────
    const renderDesktop = () => {
      const desktopCols = props.columns.filter((c) => !c.desktopHide);
      return (
        <div class="hidden w-full overflow-x-auto md:block border rounded rounded-xl overflow-hidden">
          <table class="table table-zebra w-full min-w-max border-collapse text-sm">
            <thead
              class={[
                "bg-base-100",
                props.stickyHeader === false ? "" : "sticky top-0 z-10",
              ]}
            >
              <tr class="border-b bg-base-200/40">
                {desktopCols.map((col) => (
                  <th
                    key={col.key}
                    class={`px-4 py-2.5 text-left font-semibold text-xs uppercase tracking-wider text-accent whitespace-nowrap ${col.headerClass ?? ""}`}
                  >
                    <span class="flex items-center gap-1.5">
                      {col.icon && (
                        <span class="material-symbols-outlined text-base">
                          {col.icon}
                        </span>
                      )}
                      {col.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row) => {
                const fullWidthContent = props.renderFullWidthRow?.(row);
                if (fullWidthContent != null) {
                  return (
                    <tr key={props.rowKey(row)}>
                      <td colspan={desktopCols.length} class="p-0">
                        {fullWidthContent}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={props.rowKey(row)}
                    class={`border-b transition-colors duration-75 ${
                      props.onRowClick
                        ? "cursor-pointer hover:bg-base-200/50 active:bg-base-200/80"
                        : ""
                    }`}
                    onClick={
                      props.onRowClick
                        ? () => props.onRowClick!(row)
                        : undefined
                    }
                  >
                    {desktopCols.map((col) => (
                      <td
                        key={col.key}
                        class={`px-4 py-3 text-base-content/90 ${col.cellClass ?? ""}`}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    // ─── Mobile card ─────────────────────────────────────────────────────────
    const renderMobileCard = (row: any) => {
      const fullWidthContent = props.renderFullWidthRow?.(row);
      if (fullWidthContent != null) {
        return <div key={props.rowKey(row)}>{fullWidthContent}</div>;
      }

      const primaryCols = props.columns.filter(
        (c) => c.mobilePriority === "primary",
      );
      const secondaryCols = props.columns.filter(
        (c) => c.mobilePriority === "secondary",
      );
      const actionCols = props.columns.filter(
        (c) => c.mobilePriority === "actions",
      );

      const mobileClick = props.onMobileCardClick ?? props.onRowClick;
      return (
        <div
          key={props.rowKey(row)}
          class={`rounded-lg border bg-base-200 l-box-shadow overflow-hidden mb-1.5 ${
            mobileClick
              ? "cursor-pointer active:opacity-80 transition-opacity duration-75"
              : ""
          }`}
          onClick={mobileClick ? () => mobileClick!(row) : undefined}
        >
          {/* Primary zone */}
          {primaryCols.length > 0 && (
            <div class="flex items-center gap-2 px-3 pt-2 pb-1.5">
              {primaryCols.map((col) => (
                <div
                  key={col.key}
                  class={
                    col.mobilePrimaryClass ??
                    `flex-1 min-w-0 ${col.cellClass ?? ""} !text-left`
                  }
                >
                  {col.render(row)}
                </div>
              ))}
            </div>
          )}

          {/* Secondary zone — label + value grid. Values reuse the desktop
              cellClass but are forced left: they sit under their label, so a
              right-aligned number column would float away from it. */}
          {secondaryCols.length > 0 && (
            <div class="grid grid-cols-2 gap-x-2 gap-y-1 px-3 py-1.5 border-t  bg-base-200/20">
              {secondaryCols.map((col) => (
                <div key={col.key} class="flex flex-col min-w-0">
                  <span class="text-[9px] font-semibold uppercase tracking-widest text-base-content/35">
                    {col.label}
                  </span>
                  <span
                    class={`text-xs text-base-content/75 truncate ${col.cellClass ?? ""} !text-left`}
                  >
                    {col.render(row)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Actions zone */}
          {actionCols.length > 0 && (
            <div class="px-3 py-1.5 border-t flex flex-col gap-1.5">
              {actionCols.map((col) => (
                <div key={col.key} class="w-full">
                  {col.render(row)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    const renderMobile = () => (
      <div class="md:hidden w-full px-2 pt-1.5 pb-3">
        {props.rows.map((row) => renderMobileCard(row))}
      </div>
    );

    // ─── States ───────────────────────────────────────────────────────────────
    const renderLoading = () => (
      <div class="flex justify-center items-center py-20">
        <span class="loading loading-spinner loading-lg text-primary opacity-60" />
      </div>
    );

    const renderLoadingMore = () => (
      <div class="flex justify-center items-center py-5">
        <span class="loading loading-spinner loading-sm text-primary opacity-60" />
        <span class="ml-2 text-sm text-base-content/40">Loading more…</span>
      </div>
    );

    const renderEmpty = () => (
      <div class="flex flex-col items-center justify-center py-20 gap-3 text-base-content/40">
        <span class="material-symbols-outlined text-5xl opacity-40">inbox</span>
        <p class="text-sm">{props.emptyMessage ?? "No data found"}</p>
      </div>
    );

    return () => {
      if (props.loading) return <div class="w-full">{renderLoading()}</div>;
      if (!props.rows?.length) return <div class="w-full">{renderEmpty()}</div>;

      return (
        <div class="w-full">
          {renderDesktop()}
          {renderMobile()}
          {props.loadingMore && renderLoadingMore()}
        </div>
      );
    };
  },
});
